/**
 * 注册页协议勾选组件
 * 用于注册时展示协议链接并收集用户同意
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import AgreementViewer from "./AgreementViewer";
import type { Agreement } from "@/types/consent";

interface AgreementCheckboxProps {
  /** 协议列表 */
  agreements: Agreement[];
  /** 是否已勾选 */
  checked: boolean;
  /** 勾选状态变化回调 */
  onCheckedChange: (checked: boolean) => void;
  /** 错误信息 */
  error?: string;
}

/**
 * 注册页协议勾选组件
 *
 * 功能：
 * - 显示协议链接
 * - 点击链接查看协议内容
 * - 勾选状态管理
 * - 多语言支持
 */
export default function AgreementCheckbox({
  agreements,
  checked,
  onCheckedChange,
  error,
}: AgreementCheckboxProps) {
  const { t } = useTranslation("consent");
  const [viewingAgreement, setViewingAgreement] = useState<Agreement | null>(
    null
  );

  const tosAgreement = agreements.find((a) => a.type === "tos");
  const privacyAgreement = agreements.find((a) => a.type === "privacy");

  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Checkbox
            id="agreements"
            checked={checked}
            onCheckedChange={(c) => onCheckedChange(c === true)}
          />
          <label
            htmlFor="agreements"
            className="text-xs leading-none cursor-pointer text-muted-foreground"
          >
            {t("checkbox.agreeText", "我已阅读并同意")}
            {tosAgreement && (
              <Button
                variant="link"
                className="h-auto p-0 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  setViewingAgreement(tosAgreement);
                }}
              >
                {t("checkbox.tosLink", "《服务条款》")}
              </Button>
            )}
            {tosAgreement && privacyAgreement && t("checkbox.and", "和")}
            {privacyAgreement && (
              <Button
                variant="link"
                className="h-auto p-0 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  setViewingAgreement(privacyAgreement);
                }}
              >
                {t("checkbox.privacyLink", "《隐私政策》")}
              </Button>
            )}
          </label>
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>

      {/* 协议查看弹窗 */}
      <Dialog
        open={!!viewingAgreement}
        onOpenChange={() => setViewingAgreement(null)}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {viewingAgreement?.type === "tos"
                ? t("agreement.tos", "服务条款")
                : t("agreement.privacy", "隐私政策")}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden min-h-0">
            {viewingAgreement && (
              <AgreementViewer
                agreement={viewingAgreement}
                showChangelog={false}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
